import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import AnimatedSection from '../../components/AnimatedSection';
import './LegalPages.css';

function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      <Navbar />
      <AnimatedSection animationType="fade-in">
        <div className="terms-container">
          <div className="terms-content">
            <h1>Privacy Policy</h1>
            <p><em>Last updated: July 25, 2025</em></p>

            <h2>Preamble</h2>
            <p>Third Space ("WeSplit", "we", "us", "our") places the utmost importance on the protection of your personal data. This Privacy Policy is intended to inform you in a clear, simple, and comprehensive manner about how we collect, use, and protect your data in connection with the use of our application and services (the "Service").</p>
            <p>This policy is in compliance with the legislation in force, in particular Regulation (EU) 2016/679 of the European Parliament and of the Council of April 27, 2016 (General Data Protection Regulation - GDPR) and Law No. 78-17 of January 6, 1978, known as the "Data Protection Act".</p>
            <p>We invite you to read this document carefully to understand our practices and your rights concerning your personal data.</p>

            <h2>Article 1: Identity of the Data Controller</h2>
            <table>
              <tbody>
                <tr>
                  <td><strong>Company Name</strong></td>
                  <td>Third Space</td>
                </tr>
                <tr>
                  <td><strong>Legal Form</strong></td>
                  <td>Simplified Share Business (SAS, Société par Actions Simplifiée)</td>
                </tr>
                <tr>
                  <td><strong>Share Capital</strong></td>
                  <td>1 000 €</td>
                </tr>
                <tr>
                  <td><strong>Registered Office</strong></td>
                  <td>198 D ROUTE DE NEUVILLE, 01390 SAINT-ANDRE-DE-CORCY, France</td>
                </tr>
                <tr>
                  <td><strong>Registration</strong></td>
                  <td>Registered with the Bourg-en-bresse Trade and Companies Register (RCS) under the SIREN number: 930682703</td>
                </tr>
                <tr>
                  <td><strong>Intra-Community VAT Number</strong></td>
                  <td>FR24930682703</td>
                </tr>
                <tr>
                  <td><strong>Publication Director</strong></td>
                  <td>Ms. Pauline Mila-Alonso, in her capacity as President</td>
                </tr>
                <tr>
                  <td><strong>Contact</strong></td>
                  <td>For any questions or requests for information regarding the service, or to report any illegal content or activities, the user may contact the publisher:<br />Email address: <code>contact@wesplit.io</code></td>
                </tr>
              </tbody>
            </table>

            <h2>Article 2: Personal Data Collected</h2>
            <p>In the course of providing the Service, we process different categories of data. It is essential to distinguish between two types of data:</p>
            <ol>
              <li><strong>Off-Chain Data:</strong> Data that you provide to us directly or that we generate, and which we control on our servers.
                <ul>
                  <li>Identification and contact data: Email address, pseudonym.</li>
                  <li>Identity verification (KYC) data: As part of our legal obligations, we collect information such as last name, first name, date of birth, nationality, as well as a copy of your identity document and a proof of address via our specialized provider.</li>
                  <li>Technical and connection data: IP address, device type, operating system, connection logs, information on the use of the Service.</li>
                </ul>
              </li>
              <li><strong>On-Chain Data:</strong> Data recorded on public and decentralized ledgers (Solana, Stellar blockchains).
                <ul>
                  <li>Wallet Addresses: The public address of your non-custodial wallet, which acts as a pseudonym on the blockchain.</li>
                  <li>Transaction Data: Details of your transactions (transaction ID - TxID, sender and recipient addresses, amount, date, fees).</li>
                </ul>
              </li>
            </ol>

            <h2>Article 3: Purposes and Legal Bases for Processing</h2>
            <p>Each data processing operation we perform is based on a specific purpose and a legal basis defined by the GDPR.</p>
            <table>
              <tbody>
                <tr>
                  <td><strong>Data Type</strong></td>
                  <td><strong>Processing Purposes</strong></td>
                  <td><strong>Legal Basis for Processing</strong></td>
                </tr>
                <tr>
                  <td>Identification and contact data</td>
                  <td>Creation and management of your user account.<br />Provision of customer support.<br />Sending transactional communications (confirmations, alerts).</td>
                  <td>Performance of the contract (our General Terms of Use).</td>
                </tr>
                <tr>
                  <td>Identity verification (KYC) data</td>
                  <td>To comply with our obligations to combat money laundering and terrorist financing (AML/CFT).</td>
                  <td>Legal obligation.</td>
                </tr>
                <tr>
                  <td>On-Chain Data (Wallet addresses, transactions)</td>
                  <td>Provision of the core functionalities of the Service: displaying balances, transaction history, facilitating payments and reimbursements.</td>
                  <td>Performance of the contract (our General Terms of Use).</td>
                </tr>
                <tr>
                  <td>Technical and connection data</td>
                  <td>To ensure the security of our Service, prevent fraud and abuse.<br />To improve the performance and stability of the application.<br />To produce anonymized statistics for Service improvement.</td>
                  <td>Legitimate interest of WeSplit to ensure the security and optimization of its service.</td>
                </tr>
                <tr>
                  <td>Contact data (for marketing)</td>
                  <td>Sending commercial offers, newsletters, and information about our new products.</td>
                  <td>User's consent.</td>
                </tr>
              </tbody>
            </table>

            <h2>Article 4: Specific Clause: The Nature of Data on the Blockchain</h2>
            <p>It is crucial to understand the specific nature of data recorded on a blockchain.</p>
            <blockquote>
              <p><strong>Public and Pseudonymous Nature:</strong> Your wallet address and transaction history are recorded on public ledgers (the Solana and Stellar blockchains). This data is publicly accessible but is pseudonymous: it does not directly reveal your civil identity.</p>
              <p><strong>Immutability and Limitation of Your Rights:</strong> Due to the immutable and decentralized nature of blockchain technology, transaction data, once validated and recorded, cannot technically be modified or erased. Consequently, your right to erasure ("right to be forgotten") and your right to rectification cannot apply to transaction data that has been permanently recorded on a blockchain.</p>
              <p>These rights remain fully applicable to your Off-Chain data that WeSplit controls (e.g., your email address, your profile data).</p>
            </blockquote>

            <h2>Article 5: Data Recipients</h2>
            <p>We never sell your personal data. However, we may share it with third parties in the following cases:</p>
            <ul>
              <li><strong>Our processors:</strong>
                <ul>
                  <li>Hosting providers (e.g., AWS, Google Cloud) that store our data.</li>
                  <li>KYC service provider for your identity verification.</li>
                  <li>Providers of analytics and customer support tools.</li>
                  <li>Payment service providers for managing Premium subscriptions.</li>
                </ul>
                Our processors are rigorously selected for their compliance with the GDPR and only act on our instructions.
              </li>
              <li><strong>Competent authorities:</strong>
                <ul>
                  <li>We may be required to transmit your data to judicial or administrative authorities upon legal request.</li>
                </ul>
              </li>
              <li><strong>The public (for On-Chain data):</strong>
                <ul>
                  <li>By its nature, transaction data on the blockchain is publicly accessible.</li>
                </ul>
              </li>
            </ul>

            <h2>Article 6: Data Transfers Outside the European Union</h2>
            <p>Some of our processors may be located outside the European Union. In such cases, we ensure that the transfer of your data is governed by appropriate safeguards, such as:</p>
            <ul>
              <li>The existence of an adequacy decision from the European Commission.</li>
              <li>The signing of Standard Contractual Clauses (SCCs) approved by the European Commission.</li>
            </ul>

            <h2>Article 7: Retention Periods</h2>
            <p>We retain your data for a limited period, defined according to the purpose of the processing.</p>
            <table>
              <tbody>
                <tr>
                  <td><strong>Data Category</strong></td>
                  <td><strong>Retention Period</strong></td>
                </tr>
                <tr>
                  <td>Account data (profile, email)</td>
                  <td>For the entire duration of the contractual relationship, then archived for a period of 5 years (statutory limitation period).</td>
                </tr>
                <tr>
                  <td>Identity verification (KYC) data</td>
                  <td>5 years after the end of the business relationship, in accordance with legal obligations (AML/CFT).</td>
                </tr>
                <tr>
                  <td>Connection logs and technical data</td>
                  <td>12 months maximum.</td>
                </tr>
                <tr>
                  <td>Data relating to prospects (non-customers)</td>
                  <td>3 years from your last contact.</td>
                </tr>
                <tr>
                  <td>On-Chain data (transactions)</td>
                  <td>Indefinitely, due to their immutable recording on the blockchain.</td>
                </tr>
              </tbody>
            </table>

            <h2>Article 8: Security of Your Data</h2>
            <p>The security of your data is our priority. We implement robust technical and organizational measures to protect your personal data against destruction, loss, alteration, disclosure, or unauthorized access. These measures include:</p>
            <ul>
              <li>Encryption of communications (SSL/TLS protocol).</li>
              <li>Securing our technical infrastructure.</li>
            </ul>

            <h2>Article 9: Your Rights and How to Exercise Them</h2>
            <p>In accordance with the GDPR, you have the following rights over your personal data (Off-Chain):</p>
            <ul>
              <li><strong>Right of access:</strong> To obtain a copy of the data concerning you.</li>
              <li><strong>Right to rectification:</strong> To modify inaccurate or incomplete data.</li>
              <li><strong>Right to erasure ("right to be forgotten"):</strong> To request the deletion of your data, subject to our legal obligations.</li>
              <li><strong>Right to restriction of processing:</strong> To request a temporary freeze on the use of some of your data.</li>
              <li><strong>Right to data portability:</strong> To receive your data in a structured, commonly used format to transmit it to another data controller.</li>
              <li><strong>Right to object:</strong> To object to certain processing, particularly for marketing purposes.</li>
              <li><strong>Right to withdraw your consent:</strong> For processing based on consent, you can withdraw it at any time.</li>
            </ul>
            <p>To exercise these rights, please contact us at <code>contact@wesplit.io</code>, enclosing proof of identity.</p>
            <p>If you believe that your rights have not been respected, you have the right to lodge a complaint with the French Data Protection Authority (CNIL).</p>

            <h2>Article 10: Policy on Cookies and Other Trackers</h2>
            <p>When you browse our website or application, cookies may be placed on your device. We use different types of cookies:</p>
            <ul>
              <li><strong>Strictly necessary cookies:</strong> Essential for the functioning of the Service (e.g., user session, security). They do not require your consent.</li>
              <li><strong>Performance and analytics cookies:</strong> Allow us to understand how you use the Service in order to improve it (e.g., most visited pages, loading times). They are subject to your consent.</li>
              <li><strong>Functionality cookies:</strong> Allow us to remember your preferences (e.g., language) to enhance your experience. They are subject to your consent.</li>
            </ul>
            <p>During your first visit, an information banner allows you to configure your choices regarding cookies. You can change your preferences at any time via our cookie management tool.</p>

            <h2>Article 11: Changes to the Privacy Policy</h2>
            <p>We reserve the right to modify this policy at any time, particularly to comply with legal and regulatory developments. In the event of a substantial change, we will inform you by email or via a notification in the application. The version in force is the one accessible on our website and application.</p>
          </div>
        </div>
      </AnimatedSection>
      <Footer />
    </div>
  );
}

export default Privacy;
